import React, { useEffect } from 'react';
import Chart from 'chart.js';
import 'chartjs-plugin-annotation';
// constants
import { GRAPH_DEFAULT_LINE_COLOR } from '../../constants';
import { getConvertedVerticalDistance, getConvertedDistance } from '../../utils';
export const LineGraph = ({ scatterData, infringementData, maxYValue, loading = false, type, stepSizeY, stepSizeX, lineValue = -1, flightDistance, lineColor = GRAPH_DEFAULT_LINE_COLOR, units, }) => {
    const chartRef = React.createRef();
    let closest = null;
    const minYAxis = 35000;
    let minXAxis = 150000;
    let maxValueRounded;
    if (maxYValue) {
        maxValueRounded = Math.ceil(maxYValue / 5000) * 5000;
        if (type === 'Cco') {
            maxValueRounded === 10000 ? (maxValueRounded = 15000) : (maxValueRounded = maxValueRounded);
        }
        const newMinXAxis = maxYValue / 0.15;
        minXAxis = Math.ceil(newMinXAxis / 50000) * 50000;
    }
    if (type === 'MinHeight') {
        minXAxis = 20000;
    }
    const goal = lineValue;
    if (type === 'MinHeight' && goal) {
        closest = scatterData.reduce((prev, curr) => Math.abs(curr.alt - goal) < Math.abs(prev.alt - goal) ? curr : prev);
    }
    const formatTicks = (value) => (value >= 1000 ? `${value.toString().slice(0, -3)}k` : value);
    useEffect(() => {
        const ChartElRef = chartRef.current.getContext('2d');
        if (Chart.defaults.global.legend) {
            Chart.defaults.global.legend.display = false;
        }
        Chart.defaults.global.defaultFontFamily = '-apple-system, BlinkMacSystemFont, sans-serif';
        const ChartEl = new Chart(ChartElRef, {
            type: 'scatter',
            data: {
                datasets: [
                    {
                        label: 'ScatterData',
                        fill: false,
                        // @ts-ignore
                        tension: 1,
                        pointRadius: 0,
                        pointHoverRadius: 0,
                        borderWidth: 3,
                        borderColor: lineColor,
                        showLine: true,
                        data: scatterData.map(d => ({ x: d.dist, y: d.alt, t: d.time })),
                    },
                ],
            },
            options: {
                // @ts-ignore
                animation: false,
                responsive: true,
                showLines: true,
                tooltips: {
                    callbacks: {
                        label: (tooltipItem, data) => {
                            return `Alt: ${getConvertedVerticalDistance(tooltipItem.yLabel, units.distanceVertical, 0)}`;
                        },
                        afterLabel: (tooltipItem, data) => {
                            return `Dist:  ${getConvertedDistance(tooltipItem.xLabel, units.distance, -1)}`;
                        },
                    },
                    backgroundColor: 'rgba(0,0,0,0.8)',
                    // @ts-ignore
                    bodyAlign: 'top',
                    bodyFontColor: '#fff',
                    bodySpacing: 2,
                    borderColor: 'rgba(0,0,0,0)',
                    borderWidth: 0,
                    caretPadding: 2,
                    caretSize: 5,
                    bodyFontSize: 13,
                    cornerRadius: 6,
                    displayColors: false,
                    enabled: true,
                    footerAlign: 'left',
                    footerFontColor: '#fff',
                    footerFontStyle: 'bold',
                    footerMarginTop: 6,
                    footerSpacing: 2,
                    intersect: false,
                    mode: 'nearest',
                    // @ts-ignore
                    axis: 'x',
                    position: 'nearest',
                    titleAlign: 'left',
                    titleFontColor: '#fff',
                    titleFontStyle: 'bold',
                    titleMarginBottom: 6,
                    titleSpacing: 2,
                    xPadding: 12,
                    yPadding: 12,
                },
                responsiveAnimationDuration: 0,
                elements: {
                    line: {
                        // disabling bezier because data points are so close together
                        tension: 0,
                    },
                },
                // @ts-ignore
                annotation: { annotations: [] },
                scales: {
                    xAxes: [
                        {
                            ticks: {
                                reverse: type === 'Cdo' ? true : false,
                                beginAtZero: true,
                                fontSize: 13,
                                padding: 5,
                                stepSize: stepSizeX ? stepSizeX : 50000,
                                min: 0,
                                max: minXAxis,
                                callback: (value) => formatTicks(value),
                            },
                            gridLines: {
                                borderDash: [4],
                                zeroLineBorderDash: [4],
                                lineWidth: 1,
                                drawBorder: false,
                                color: '#dfe4e8',
                                zeroLineColor: '#dfe4e8',
                                zeroLineWidth: 1,
                            },
                        },
                    ],
                    yAxes: [
                        {
                            gridLines: {
                                tickMarkLength: 15,
                                zeroLineColor: '#dfe4e8',
                                zeroLineWidth: 3,
                                lineWidth: 2,
                                drawBorder: false,
                            },
                            ticks: {
                                fontSize: 13,
                                padding: 5,
                                stepSize: stepSizeY ? stepSizeY : 5000,
                                max: maxYValue ? maxValueRounded : minYAxis,
                                callback: (value) => formatTicks(value),
                            },
                        },
                    ],
                },
            },
        });
        if (infringementData) {
            infringementData.map((item, index) => {
                // @ts-ignore
                ChartEl.options.annotation.annotations.push({
                    type: 'box',
                    drawTime: 'beforeDatasetsDraw',
                    id: `a-box-${index}`,
                    xScaleID: 'x-axis-1',
                    yScaleID: 'y-axis-1',
                    xMin: item.startDistance,
                    xMax: item.endDistance,
                    yMax: maxYValue ? maxValueRounded : minYAxis,
                    yMin: 0,
                    borderColor: 'transparent',
                    backgroundColor: 'rgba(0,0,0, 0.1)',
                });
            });
            ChartEl.update();
        }
        if (type === 'MinHeight') {
            // @ts-ignore
            ChartEl.options.annotation.annotations.push({
                type: 'line',
                mode: 'horizontal',
                scaleID: 'y-axis-1',
                value: lineValue,
                borderColor: '#bd1427',
                borderDash: [4, 4],
                borderWidth: 2,
                label: {
                    enabled: false,
                    content: 'Test label',
                },
            });
            // @ts-ignore
            ChartEl.options.annotation.annotations.push({
                type: 'box',
                drawTime: 'beforeDatasetsDraw',
                id: `a-box-${1}`,
                xScaleID: 'x-axis-1',
                yScaleID: 'y-axis-1',
                xMin: flightDistance,
                xMax: closest.x,
                yMax: maxYValue ? maxValueRounded : minYAxis,
                yMin: 0,
                borderColor: 'transparent',
                backgroundColor: 'rgba(0,0,0, 0.1)',
            });
            ChartEl.update();
        }
        if (type === 'Corridor' && lineValue >= 0) {
            // @ts-ignore
            ChartEl.options.annotation.annotations.push({
                type: 'line',
                mode: 'vertical',
                scaleID: 'x-axis-1',
                value: lineValue,
                borderColor: '#8897a2',
                borderWidth: 2,
                label: {
                    enabled: false,
                },
            });
            ChartEl.update();
        }
        return () => {
            ChartEl.destroy();
        };
    }, [scatterData, lineValue]);
    if (loading) {
        return React.createElement("div", { className: "line-graph--loading" });
    }
    return (React.createElement("div", null,
        React.createElement("canvas", { id: "LineGraph", ref: chartRef })));
};
//# sourceMappingURL=LineGraph.js.map